import ReactDOM from 'react-dom';
import * as Sentry from '@sentry/react';

import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';

import * as serviceWorker from 'serviceWorker';
import App from 'App';
import { store } from 'store';

import 'assets/scss/style.scss';
import { ApolloProvider } from '@apollo/client';
import client from 'helper/helper';

// Initialize Sentry only if DSN is provided
if (process.env.REACT_APP_SENTRY_DSN) {
    Sentry.init({
        dsn: process.env.REACT_APP_SENTRY_DSN,
        environment: process.env.NODE_ENV || 'development',
        // Performance monitoring
        tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,
        // Error sampling
        sampleRate: 1.0,
        // Release tracking
        release: process.env.REACT_APP_VERSION || 'unknown',
        // Set up integrations
        integrations: [
            Sentry.browserTracingIntegration({
                tracePropagationTargets: ['localhost', /^https:\/\/.*\.diolog\.ai/, /^https:\/\/.*\.vercel\.app/]
            }),
            Sentry.captureConsoleIntegration({
                levels: ['error', 'warn']
            })
        ],
        // Data scrubbing for sensitive information
        beforeSend(event) {
            // Filter out sensitive data from requests
            if (event.request?.cookies) {
                delete event.request.cookies;
            }
            if (event.request?.headers?.authorization) {
                event.request.headers.authorization = '[Filtered]';
            }
            if (event.request?.headers?.Authorization) {
                event.request.headers.Authorization = '[Filtered]';
            }
            // Filter sensitive data from breadcrumbs
            if (event.breadcrumbs) {
                event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => {
                    if (breadcrumb.data) {
                        ['password', 'token', 'refreshtoken', 'authorization', 'accessToken', 'refreshToken'].forEach((key) => {
                            if (breadcrumb.data[key]) {
                                breadcrumb.data[key] = '[Filtered]';
                            }
                        });
                    }
                    return breadcrumb;
                });
            }
            // Filter sensitive query variables from extra data
            if (event.extra) {
                ['password', 'token', 'refreshtoken', 'authorization', 'accessToken', 'refreshToken'].forEach((key) => {
                    if (event.extra[key]) {
                        event.extra[key] = '[Filtered]';
                    }
                });
            }
            return event;
        },
        // Don't capture errors in development unless explicitly needed
        enabled: process.env.NODE_ENV === 'production' || process.env.REACT_APP_SENTRY_DEBUG === 'true'
    });
} else {
    console.warn(
        'Sentry DSN not configured. Error monitoring is disabled. Set REACT_APP_SENTRY_DSN environment variable to enable Sentry.'
    );
}

const AppWithErrorBoundary = () => {
    const ErrorFallback = ({ error, resetError }) => (
        <div style={{ padding: '20px', textAlign: 'center' }}>
            <h2>Something went wrong</h2>
            <p>We are sorry, but something unexpected happened{process.env.REACT_APP_SENTRY_DSN ? '. Our team has been notified' : ''}.</p>
            <button type="button" onClick={resetError} style={{ margin: '10px', padding: '10px 20px' }}>
                Try again
            </button>
            <button type="button" onClick={() => window.location.reload()} style={{ margin: '10px', padding: '10px 20px' }}>
                Refresh page
            </button>
            {process.env.NODE_ENV === 'development' && (
                <details style={{ marginTop: '20px', textAlign: 'left' }}>
                    <summary>Error details (development only)</summary>
                    <pre style={{ background: '#f5f5f5', padding: '10px', marginTop: '10px' }}>{error.toString()}</pre>
                </details>
            )}
        </div>
    );

    if (process.env.REACT_APP_SENTRY_DSN) {
        return (
            <Sentry.ErrorBoundary fallback={ErrorFallback} showDialog>
                <App />
            </Sentry.ErrorBoundary>
        );
    }

    // Basic error boundary when Sentry is not available
    return <App />;
};

ReactDOM.render(
    <Provider store={store}>
        <BrowserRouter>
            <ApolloProvider client={client}>
                <AppWithErrorBoundary />
            </ApolloProvider>
        </BrowserRouter>
    </Provider>,
    document.getElementById('root')
);
serviceWorker.unregister();
